import React, { useMemo, useState } from 'react';
import { Upload, FileText, CheckCircle, XCircle, Users, AtSign, Phone, Trash2 } from 'lucide-react';

const UploadPage = ({
  dragActive,
  handleDrag,
  handleDrop,
  handleFileInput,
  uploads,
  contacts,
  contactsTotal,
  deleteUploadRecord,
  setCurrentPage
}) => {
  const recentContacts = useMemo(() => contacts.slice(0, 10), [contacts]);
  const [uploadPendingDeletion, setUploadPendingDeletion] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Upload & Parse Contacts</h1>
        <p className="text-gray-600 mt-1">Import contacts from mbox, CSV, or text files with automatic extraction</p>
      </div>

      {/* Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-3 border-dashed rounded-2xl p-12 text-center transition-all duration-300 ${
          dragActive 
            ? 'border-indigo-500 bg-indigo-50 scale-[1.02] shadow-xl' 
            : 'border-gray-300 bg-white hover:border-indigo-400 hover:shadow-lg'
        }`}
      >
        <Upload className={`mx-auto mb-4 ${dragActive ? 'text-indigo-600 animate-bounce' : 'text-gray-400'}`} size={48} />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Drop your files here</h3>
        <p className="text-gray-600 mb-4">Automatic extraction of emails and phone numbers</p>
        <input
          type="file"
          multiple
          accept=".mbox,.csv,.txt"
          onChange={handleFileInput}
          className="hidden"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className="inline-block px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold cursor-pointer hover:shadow-lg transition-all duration-300"
        >
          Choose Files
        </label>
        <p className="text-sm text-gray-500 mt-4">Supports .mbox, .csv, and .txt files • Automatic deduplication</p>
      </div>

      {/* Upload History */}
      {uploads.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Upload History</h2>
            <span className="text-sm text-gray-600">{uploads.length} file(s)</span>
          </div>
          <div className="space-y-3">
            {uploads.map(upload => (
              <div key={upload.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-indigo-50 rounded-xl hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-3 flex-1">
                  <FileText className="text-indigo-600 flex-shrink-0" size={20} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{upload.filename}</div>
                    <div className="text-sm text-gray-600">
                      {upload.status === 'completed' 
                        ? `${upload.count} extracted • ${upload.newCount} new • ${upload.updated || 0} updated • ${upload.duplicates} duplicates` 
                        : upload.status === 'processing' 
                        ? 'Processing...' 
                        : `Failed: ${upload.error}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {upload.status === 'completed' ? (
                    <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
                  ) : upload.status === 'processing' ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                  ) : (
                    <XCircle className="text-red-600 flex-shrink-0" size={20} />
                  )}
                  <button
                    onClick={() => {
                      setUploadPendingDeletion(upload);
                      setDeleteConfirmation('');
                    }}
                    disabled={upload.status === 'processing'}
                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Delete imported contacts"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recently Extracted Contacts */}
      {recentContacts.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              Recently Extracted ({recentContacts.length} shown of {contactsTotal.toLocaleString()})
            </h2>
            <button
              onClick={() => setCurrentPage('contacts')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 flex items-center gap-2"
            >
              <Users size={18} />
              View All Contacts
            </button>
          </div>
          <div className="space-y-2">
            {recentContacts.map(contact => (
              <div key={contact.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${contact.email ? 'bg-indigo-100' : 'bg-purple-100'} rounded-lg`}>
                    {contact.email 
                      ? <AtSign className="text-indigo-600" size={16} /> 
                      : <Phone className="text-purple-600" size={16} />
                    }
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{contact.email || contact.phone}</div>
                    {contact.firstName && (
                      <div className="text-sm text-gray-600">{contact.firstName} {contact.lastName}</div>
                    )}
                    {Array.isArray(contact.tags) && contact.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {contact.tags.slice(0, 3).map((tag) => (
                          <span key={`${contact.id}-${tag}`} className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold">
                            {tag}
                          </span>
                        ))}
                        {contact.tags.length > 3 && (
                          <span className="text-xs text-gray-500">+{contact.tags.length - 3} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-600">{contact.source}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {uploadPendingDeletion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Confirm Deletion</h3>
              <p className="mt-2 text-sm text-gray-600">
                This will permanently delete all contacts imported from <span className="font-semibold text-red-600">{uploadPendingDeletion.filename}</span>.
                Type <span className="font-semibold text-red-600">DELETE</span> to confirm.
              </p>
            </div>
            <input
              type="text"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setUploadPendingDeletion(null);
                  setDeleteConfirmation('');
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const success = await deleteUploadRecord(uploadPendingDeletion, deleteConfirmation.trim());
                  if (success) {
                    setUploadPendingDeletion(null);
                    setDeleteConfirmation('');
                  }
                }}
                disabled={deleteConfirmation.trim() !== 'DELETE'}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold shadow hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadPage;
